import { get } from 'lodash'

import { getJobStatusesCached } from '@/lib/api/jobs/jobStatusesCache'
import type { JobStatusRow } from '@/schemas/jobs/schema'
import { checkCondition } from '@/modules/util'
import type { BookOrder, Filter, JobStatus } from '@/types'

import { getLittaJobStatusColour } from './littaJobStatusColours'
import {
    LITTA_UNASSIGNED_CARRIER_KEYS,
    type LittaUnassignedCarrierStatusKey,
} from './littaUnassignedStatusKeys'
import { LITTA_TERMINAL_STATUS_KEYS } from './littaTerminalStatusKeys'

export {
    isLittaUnassignedCarrierStatus,
    LITTA_UNASSIGNED_CARRIER_KEYS,
    type LittaUnassignedCarrierStatusKey,
} from './littaUnassignedStatusKeys'
export {
    isLittaTerminalStatus,
    LITTA_TERMINAL_STATUS_KEYS,
    type LittaTerminalStatusKey,
} from './littaTerminalStatusKeys'

export const TOOKAN_TASK_STATUS_COLUMN = 'appJobDetails.tookanTaskStatus'

/** Canonical LITTA `job_status.key` → equivalent Tookan label (Beehive source of truth). */
export const LITTA_KEY_TO_TOOKAN_LABEL: Record<string, string> = {
    ready_to_offer: 'Unassigned',
    offered: 'Unassigned',
    offer_accepted: 'Accepted',
    offer_rejected: 'Declined',
    assigned: 'Assigned',
    started: 'Started',
    en_route: 'Started',
    arrived: 'InProgress',
    acknowledged: 'InProgress',
    before_photos_captured: 'InProgress',
    after_photos_captured: 'InProgress',
    signature_reviewed: 'InProgress',
    complete: 'Successful',
    failed: 'Failed',
    cancelled: 'Cancelled',
}

/** @deprecated Use LITTA_KEY_TO_TOOKAN_LABEL — terminal filter labels only. */
export const TOOKAN_LABEL_TO_STATUS_KEY: Record<string, string> = {
    Successful: 'complete',
    Cancelled: 'cancelled',
    Failed: 'failed',
}

const buildTookanLabelToLittaKeys = (): Record<string, string[]> => {
    const map: Record<string, string[]> = {}
    for (const [key, label] of Object.entries(LITTA_KEY_TO_TOOKAN_LABEL)) {
        if (!map[label]) {
            map[label] = []
        }
        map[label].push(key)
    }
    return map
}

/** Tookan label → all matching LITTA `job_status.key` values. */
export const TOOKAN_LABEL_TO_LITTA_KEYS = buildTookanLabelToLittaKeys()

export type DashboardStatusBucket =
    'notStarted' | 'started' | 'inProgress' | 'complete' | 'cancelled'

/** Dashboard bucket → LITTA keys (derived from canonical Tookan label mapping). */
export const LITTA_DASHBOARD_BUCKET_KEYS: Record<
    DashboardStatusBucket,
    readonly string[]
> = {
    notStarted: [
        'ready_to_offer',
        'offered',
        'offer_rejected',
        'offer_accepted',
        'assigned',
    ],
    started: ['started', 'en_route'],
    inProgress: [
        'arrived',
        'acknowledged',
        'before_photos_captured',
        'after_photos_captured',
        'signature_reviewed',
    ],
    complete: ['complete', 'failed'],
    cancelled: ['cancelled'],
}

/** Dashboard Total bucket — operational jobs only (excludes failed and cancelled). */
export const LITTA_DASHBOARD_TOTAL_KEYS = [
    ...LITTA_DASHBOARD_BUCKET_KEYS.notStarted,
    ...LITTA_DASHBOARD_BUCKET_KEYS.started,
    ...LITTA_DASHBOARD_BUCKET_KEYS.inProgress,
    'complete',
] as const

/**
 * Tookan numeric status ID → canonical LITTA `job_status.key`.
 * Aligns with {@link TOOKAN_STATUS_IDS_BY_DASHBOARD_BUCKET} in dashboardStatusQueries.
 */
export const TOOKAN_STATUS_ID_TO_KEY: Record<number, string> = {
    0: 'ready_to_offer',
    1: 'started',
    2: 'complete',
    3: 'failed',
    4: 'arrived',
    6: 'offer_accepted',
    7: 'offer_accepted',
    9: 'cancelled',
}

/** Tookan-only labels without a 1:1 entry in LITTA_KEY_TO_TOOKAN_LABEL. */
const TOOKAN_ONLY_LABEL_TO_KEY: Record<string, string> = {
    Declined: 'offer_rejected',
    Deleted: 'cancelled',
    'Successful - OFF APP': 'complete',
    'Wasted Journey': 'failed',
}

/** LITTA Assign modal: Revoke offer / Offer flow (not deallocation). */
export const LITTA_OFFER_FLOW_STATUS_KEYS = LITTA_UNASSIGNED_CARRIER_KEYS

/** LITTA Assign modal: statuses that show Deallocate / Deallocate and offer. */
export const LITTA_DEALLOCATE_STATUS_KEYS = [
    'offer_accepted',
    'assigned',
    'acknowledged',
    'started',
    'en_route',
    'arrived',
    'before_photos_captured',
    'after_photos_captured',
    'failed',
] as const

/** Tookan fulfilment: pre-acceptance keys (excludes `assigned`). */
const TOOKAN_UNASSIGNED_CARRIER_KEYS = ['ready_to_offer', 'offered'] as const

export interface JobStatusLookup {
    idByKey: Map<string, string>
    keyById: Map<string, string>
    idByTookanLabel: Map<string, string>
    nameById: Map<string, string>
    nameByKey: Map<string, string>
    categoryById: Map<string, string>
    colourById: Map<string, string>
    textColourById: Map<string, string>
}

const buildLookup = (statuses: JobStatusRow[]): JobStatusLookup => {
    const idByKey = new Map<string, string>()
    const keyById = new Map<string, string>()
    const nameById = new Map<string, string>()
    const nameByKey = new Map<string, string>()
    const categoryById = new Map<string, string>()
    const colourById = new Map<string, string>()
    const textColourById = new Map<string, string>()

    for (const row of statuses) {
        idByKey.set(row.key, row.id)
        keyById.set(row.id, row.key)
        nameById.set(row.id, row.name)
        nameByKey.set(row.key, row.name)
        if (row.category) {
            categoryById.set(row.id, row.category)
        }
        if (row.displayColour) {
            colourById.set(row.id, row.displayColour)
        }
        if (row.displayTextColour) {
            textColourById.set(row.id, row.displayTextColour)
        }
    }

    const idByTookanLabel = new Map<string, string>()
    for (const [label, keys] of Object.entries(TOOKAN_LABEL_TO_LITTA_KEYS)) {
        const firstId = keys
            .map(key => idByKey.get(key))
            .find((id): id is string => !!id)
        if (firstId) {
            idByTookanLabel.set(label, firstId)
        }
    }

    return {
        categoryById,
        colourById,
        textColourById,
        idByKey,
        idByTookanLabel,
        keyById,
        nameById,
        nameByKey,
    }
}

let cachedLookup: JobStatusLookup | null = null
let lookupInflight: Promise<JobStatusLookup> | null = null

export async function loadJobStatusLookup(): Promise<JobStatusLookup> {
    if (cachedLookup) {
        return cachedLookup
    }
    if (lookupInflight) {
        return lookupInflight
    }

    lookupInflight = getJobStatusesCached()
        .then(rows => {
            cachedLookup = buildLookup(rows)
            return cachedLookup
        })
        .finally(() => {
            lookupInflight = null
        })

    return lookupInflight
}

export const isLittaFulfilmentChannel = (
    channel: string | undefined,
): boolean => channel === 'litta'

/** LITTA job has statusId but lookup is unavailable — status cannot be resolved. */
export function isLittaStatusIndeterminate(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    return (
        isLittaFulfilmentChannel(order.fulfilmentChannel) &&
        !!order.appJobDetails?.statusId &&
        !lookup
    )
}

export function getLittaStatusKey(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): string | undefined {
    const statusId = order.appJobDetails?.statusId
    if (!statusId || !lookup) {
        return undefined
    }
    return lookup.keyById.get(statusId)
}

function getTookanCanonicalStatusKey(order: BookOrder): string | undefined {
    const statusId = order.appJobDetails?.tookanTaskStatusId
    if (statusId != null && TOOKAN_STATUS_ID_TO_KEY[statusId]) {
        return TOOKAN_STATUS_ID_TO_KEY[statusId]
    }

    const label = order.appJobDetails?.tookanTaskStatus
    if (!label) {
        return undefined
    }

    const onlyLabelKey = TOOKAN_ONLY_LABEL_TO_KEY[label]
    if (onlyLabelKey) {
        return onlyLabelKey
    }

    const deprecatedKey = TOOKAN_LABEL_TO_STATUS_KEY[label]
    if (deprecatedKey) {
        return deprecatedKey
    }

    const mappedKeys = TOOKAN_LABEL_TO_LITTA_KEYS[label]
    return mappedKeys?.[0]
}

/**
 * Normalise any job to a canonical LITTA `job_status.key`.
 * LITTA jobs use `statusId` only; Tookan jobs use numeric ID then label fallback.
 */
export function getCanonicalStatusKey(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): string | undefined {
    if (isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        return getLittaStatusKey(order, lookup)
    }
    return getTookanCanonicalStatusKey(order)
}

/**
 * Pre-canonical Beehive table rule for Tookan jobs and orders with no
 * `fulfilmentChannel`. Uses raw `tookanTaskStatus` labels so legacy rows
 * (e.g. Assigned but not accepted) still group under "Not assigned/accepted".
 */
function isLegacyTookanUnassignedByDisplayLabel(order: BookOrder): boolean {
    if (order?.offApp) {
        return false
    }

    const status = order.appJobDetails?.tookanTaskStatus ?? ''
    if (status === 'Declined') {
        return true
    }

    return ['Unassigned', 'Assigned'].includes(status) && !order?.isAccepted
}

/** Job should show as not assigned/accepted in carrier columns. */
export function isUnassignedForCarrierDisplay(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    if (isLittaStatusIndeterminate(order, lookup)) {
        return false
    }

    if (isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        const key = getCanonicalStatusKey(order, lookup)
        if (!key) {
            return true
        }
        return LITTA_UNASSIGNED_CARRIER_KEYS.includes(
            key as (typeof LITTA_UNASSIGNED_CARRIER_KEYS)[number],
        )
    }

    if (isLegacyTookanUnassignedByDisplayLabel(order)) {
        return true
    }

    const key = getCanonicalStatusKey(order, lookup)
    if (!key) {
        return true
    }

    if (key === 'offer_rejected') {
        return true
    }
    if (
        TOOKAN_UNASSIGNED_CARRIER_KEYS.includes(
            key as (typeof TOOKAN_UNASSIGNED_CARRIER_KEYS)[number],
        ) &&
        !order?.isAccepted &&
        !order?.offApp
    ) {
        return true
    }
    return false
}

/** Job should show as assigned/accepted in carrier columns. */
export function isAssignedForCarrierDisplay(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    if (order?.offApp) {
        return true
    }

    const key = getCanonicalStatusKey(order, lookup)
    if (!key) {
        return false
    }
    if (key === 'assigned' || key === 'offer_accepted') {
        return true
    }
    if ((key === 'ready_to_offer' || key === 'offered') && order?.isAccepted) {
        return true
    }
    return false
}

/** Dashboard bucket check for any fulfilment channel via canonical key. */
export function isStatusInDashboardBucket(
    order: BookOrder,
    bucket: DashboardStatusBucket,
    lookup: JobStatusLookup | null,
): boolean {
    if (isLittaStatusIndeterminate(order, lookup)) {
        return false
    }

    const key = getCanonicalStatusKey(order, lookup)
    if (!key) {
        return false
    }
    return LITTA_DASHBOARD_BUCKET_KEYS[bucket].includes(key)
}

export function getLittaStatusIdsForKeys(
    keys: readonly string[],
    lookup: JobStatusLookup,
): string[] {
    return keys
        .map(key => lookup.idByKey.get(key))
        .filter((id): id is string => !!id)
}

export function getLittaStatusIdsForTookanLabel(
    label: string,
    lookup: JobStatusLookup,
): string[] {
    const keys = TOOKAN_LABEL_TO_LITTA_KEYS[label]
    if (!keys?.length) {
        return []
    }
    return getLittaStatusIdsForKeys(keys, lookup)
}

export function getLittaStatusIdForTookanLabel(
    label: string,
    lookup: JobStatusLookup,
): string | null {
    const ids = getLittaStatusIdsForTookanLabel(label, lookup)
    return ids[0] ?? null
}

export function getTookanLabelForLittaStatusId(
    statusId: string | undefined,
    lookup: JobStatusLookup,
): string | null {
    if (!statusId) {
        return null
    }
    const key = lookup.keyById.get(statusId)
    if (!key) {
        return null
    }
    return LITTA_KEY_TO_TOOKAN_LABEL[key] ?? null
}

/**
 * @deprecated Legacy display only — maps LITTA status to Tookan-shaped label.
 * Do not use for business logic; prefer `resolveDisplayTaskStatus` or `getCanonicalStatusKey`.
 */
export function resolveTookanEquivalentLabel(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): string | undefined {
    if (!isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        return order.appJobDetails?.tookanTaskStatus
    }
    if (!lookup) {
        return undefined
    }
    const key = getLittaStatusKey(order, lookup)
    if (!key) {
        return undefined
    }
    return LITTA_KEY_TO_TOOKAN_LABEL[key]
}

export function resolveJobStatusColour(
    order: BookOrder,
    lookup: JobStatusLookup | null,
    tookanJobStatuses: JobStatus[] | null | undefined,
): string {
    if (isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        const key = getLittaStatusKey(order, lookup)
        return getLittaJobStatusColour(key)
    }

    const statusName = order.appJobDetails?.tookanTaskStatus
    if (!statusName) {
        return 'var(--p-neutral)'
    }

    const jobStatus = tookanJobStatuses?.find(
        status => status.status === statusName,
    )
    return jobStatus?.markerColourHex ?? 'var(--p-neutral)'
}

export function matchesTaskStatusFilter(
    order: BookOrder,
    filter: Filter,
    lookup: JobStatusLookup,
): boolean {
    if (filter.column !== TOOKAN_TASK_STATUS_COLUMN) {
        return true
    }

    const filterValue = String(filter.value)

    if (isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        const statusId = order.appJobDetails?.statusId
        const expectedIds = getLittaStatusIdsForTookanLabel(filterValue, lookup)
        if (expectedIds.length === 0) {
            return false
        }
        if (filter.operator === '=') {
            return expectedIds.includes(statusId ?? '')
        }
        if (filter.operator === '!=') {
            return !expectedIds.includes(statusId ?? '')
        }
        return expectedIds.some(id =>
            checkCondition(statusId, id, filter.operator),
        )
    }

    return checkCondition(
        get(order, TOOKAN_TASK_STATUS_COLUMN),
        filterValue,
        filter.operator,
    )
}

export function resolveDisplayTaskStatus(
    order: BookOrder,
    lookup: JobStatusLookup,
): string {
    if (isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        const statusId = order.appJobDetails?.statusId
        if (!statusId) {
            return 'Unknown'
        }
        return lookup.nameById.get(statusId) ?? 'Unknown'
    }
    return order.appJobDetails?.tookanTaskStatus ?? 'Unknown'
}

/** Display task status; LITTA jobs show Loading until lookup is available. */
export function resolveDisplayTaskStatusWithLookup(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): string {
    if (isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        if (!lookup) {
            return 'Loading...'
        }
        return resolveDisplayTaskStatus(order, lookup)
    }
    return order.appJobDetails?.tookanTaskStatus ?? 'Unknown'
}

/** Haystack-aligned status label for dashboard filters and lateness checks. */
export function resolveDisplayTaskStatusForFilters(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): string {
    if (isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        const tookanLabel = resolveTookanEquivalentLabel(order, lookup)
        if (tookanLabel) {
            return tookanLabel
        }
        return resolveDisplayTaskStatusWithLookup(order, lookup)
    }

    let computedStatus = order.appJobDetails?.tookanTaskStatus ?? 'Unknown'
    const isStatusMet = ['Unassigned', 'Assigned'].includes(computedStatus)

    if (isStatusMet && order.isAccepted) {
        computedStatus = 'Accepted'
    } else if (isStatusMet && !order.isAccepted && !order.offApp) {
        computedStatus = 'Unassigned'
    }

    return computedStatus
}

/** Whether a job can appear in Haystack-style lateness groupings. */
export function isJobLateEligible(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    if (isLittaFulfilmentChannel(order.fulfilmentChannel) && !lookup) {
        return false
    }

    const status = resolveDisplayTaskStatusForFilters(order, lookup)
    if (!status || status === 'Loading...') {
        return false
    }

    return !['Successful', 'Wasted Journey', 'Cancelled'].includes(status)
}

export function resolveStatusPillDisplay(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): { label: string; detail: string | null } {
    const label = resolveDisplayTaskStatusWithLookup(order, lookup)
    if (!isLittaFulfilmentChannel(order.fulfilmentChannel) || !lookup) {
        return { label, detail: null }
    }
    const statusId = order.appJobDetails?.statusId
    if (statusId && lookup.categoryById.get(statusId) === 'active') {
        return { label: 'In progress', detail: label }
    }
    return { label, detail: null }
}

/** Value used by ops dashboard Latest Status card rules (`statusId` or display name). */
export function resolveLatestStatusFilterValue(
    order: BookOrder,
    lookup: JobStatusLookup | null,
    key: string,
): string | number | undefined {
    if (key === 'statusId') {
        if (isLittaFulfilmentChannel(order.fulfilmentChannel)) {
            return order.appJobDetails?.statusId
        }
        return order.appJobDetails?.tookanTaskStatusId
    }
    return resolveDisplayTaskStatusWithLookup(order, lookup)
}

export interface LatestJobStatusSummary {
    label: string
    rawValue: string
    /** When set, render as coloured badge; omit for plain/loading/unknown text. */
    badgeColour?: string
}

export function resolveLatestJobStatusSummary(
    order: BookOrder,
    lookup: JobStatusLookup | null,
    tookanJobStatuses: JobStatus[] | null | undefined,
): LatestJobStatusSummary {
    if (isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        if (!lookup) {
            return { label: 'Loading...', rawValue: 'Loading...' }
        }

        const displayName = resolveDisplayTaskStatus(order, lookup)
        if (displayName === 'Unknown') {
            return { label: 'Unknown', rawValue: 'Unknown' }
        }

        return {
            label: displayName,
            rawValue: displayName,
            badgeColour: resolveJobStatusColour(
                order,
                lookup,
                tookanJobStatuses,
            ),
        }
    }

    const latestStatus = order.appJobDetails?.tookanTaskStatus
    if (!latestStatus) {
        return { label: 'Unknown', rawValue: 'Unknown' }
    }

    return {
        label: latestStatus,
        rawValue: latestStatus,
        badgeColour: resolveJobStatusColour(order, lookup, tookanJobStatuses),
    }
}

export function isJobFailedStatus(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    return getCanonicalStatusKey(order, lookup) === 'failed'
}

export function isLittaDeallocateEligible(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    if (!isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        return false
    }
    if (!order.appJobDetails?.partnerId) {
        return false
    }
    const key = getLittaStatusKey(order, lookup)
    if (!key) {
        return false
    }
    return LITTA_DEALLOCATE_STATUS_KEYS.includes(
        key as (typeof LITTA_DEALLOCATE_STATUS_KEYS)[number],
    )
}

export function isLittaRevokeOfferStatus(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    if (!isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        return false
    }
    const key = getLittaStatusKey(order, lookup)
    if (!key) {
        return false
    }
    return LITTA_OFFER_FLOW_STATUS_KEYS.includes(
        key as (typeof LITTA_OFFER_FLOW_STATUS_KEYS)[number],
    )
}

export function canLittaRevokeOffer(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    if (!isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        return false
    }
    if (!order.appJobDetails?.partnerId) {
        return false
    }
    const statusId = order.appJobDetails.statusId
    if (!statusId) {
        return true
    }
    const key = getLittaStatusKey(order, lookup)
    if (!key) {
        return false
    }
    return isLittaRevokeOfferStatus(order, lookup)
}

/** LITTA Assign modal: Offer / Deallocate and offer allowed statuses only. */
export function isLittaOfferActionAllowed(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    if (!isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        return true
    }
    if (!order.appJobDetails?.statusId) {
        return true
    }
    if (!lookup) {
        return false
    }
    return (
        isLittaDeallocateEligible(order, lookup) ||
        isLittaRevokeOfferStatus(order, lookup)
    )
}

export function isJobCompleteStatus(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    return getCanonicalStatusKey(order, lookup) === 'complete'
}

export function isJobCancelledStatus(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    return getCanonicalStatusKey(order, lookup) === 'cancelled'
}

export function isJobTerminalStatus(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    return (
        isJobCompleteStatus(order, lookup) ||
        isJobFailedStatus(order, lookup) ||
        isJobCancelledStatus(order, lookup)
    )
}

/**
 * Legacy slot-pre-check terminal semantics: missing Tookan label was treated as Successful.
 */
export function isJobTerminalForSlotPreCheck(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    if (isJobTerminalStatus(order, lookup)) {
        return true
    }
    if (
        !isLittaFulfilmentChannel(order.fulfilmentChannel) &&
        order.appJobDetails?.tookanTaskStatus == null
    ) {
        return true
    }
    return false
}

export function isJobIncludedInDashboardTotal(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    if (
        isJobFailedStatus(order, lookup) ||
        isJobCancelledStatus(order, lookup)
    ) {
        return false
    }
    if (isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        return true
    }
    const statusId = order.appJobDetails?.tookanTaskStatusId
    if (statusId == null) {
        return false
    }
    return ![8, 10].includes(statusId)
}

export function isPostReviewFirestoreEligible(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    if (isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        const key = getLittaStatusKey(order, lookup)
        return (
            !!key &&
            (LITTA_TERMINAL_STATUS_KEYS as readonly string[]).includes(key)
        )
    }
    const statusId = order.appJobDetails?.tookanTaskStatusId
    return statusId === 2 || statusId === 3 || statusId === 9
}

export function isJobReviewTabEligible(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    return isJobCompleteStatus(order, lookup)
}

const PRE_REVIEW_EXCLUDED_TOOKAN_LABELS = [
    'Cancelled',
    'Successful',
    'Wasted Journey',
    'Failed',
    'Successful - OFF APP',
] as const

export function isJobExcludedFromPreReview(
    order: BookOrder,
    lookup: JobStatusLookup | null,
): boolean {
    if (isLittaStatusIndeterminate(order, lookup)) {
        return false
    }
    if (isLittaFulfilmentChannel(order.fulfilmentChannel)) {
        return isJobTerminalStatus(order, lookup)
    }
    return PRE_REVIEW_EXCLUDED_TOOKAN_LABELS.includes(
        order.appJobDetails
            ?.tookanTaskStatus as (typeof PRE_REVIEW_EXCLUDED_TOOKAN_LABELS)[number],
    )
}

/** @deprecated Use {@link isStatusInDashboardBucket} */
export function isLittaStatusInDashboardBucket(
    order: BookOrder,
    bucket: DashboardStatusBucket,
    lookup: JobStatusLookup | null,
): boolean {
    return isStatusInDashboardBucket(order, bucket, lookup)
}

export function getTaskStatusFilters(filters: Filter[] | undefined): Filter[] {
    return (
        filters?.filter(
            filter => filter.column === TOOKAN_TASK_STATUS_COLUMN,
        ) ?? []
    )
}

export function hasTaskStatusFilters(filters: Filter[] | undefined): boolean {
    return getTaskStatusFilters(filters).length > 0
}

/** Filters applied in Firestore converters (excludes date, client_type, paid, task status). */
export function getTableConverterFilters(
    filters: Filter[] | undefined,
): Filter[] {
    return (
        filters?.filter(
            filter =>
                ![
                    'selectedDateAndTime',
                    'client_type',
                    'created_at',
                    'paid',
                    TOOKAN_TASK_STATUS_COLUMN,
                ].includes(filter.column),
        ) ?? []
    )
}
